/** @jsxRuntime classic */
/** @jsx jsx */
import { FC } from 'react';
import { jsx } from '@emotion/react';
import { QuestionDto } from '@bondsports/types';
import { ETypography, ETypographyColor, Icons } from '../../../types';
import { CHARACHERS } from '../../../lib/constants';
import { resolveApplicationTemplateQuestionBody } from '../../../lib/form';
import {
	collectFileRefs,
	getAnswerText,
	IFileRef,
	isAllowedPrintImageUrl,
	isConsentQuestion,
	isImageFile,
	isYesAnswer,
	unwrapAnswerValue,
} from '../../../lib/formats/answer';
import { RCCustomQuestionType, RCQuestionTypesEnum } from '../../../types/entities/coreEntites/Questions';
import { i18n } from '../../../lib/i18n';
import { Typography } from '../../../atoms';
import { BnIcon } from '../../../components/icons_v2';
import { answerCss } from '../styles';

interface IProps {
	answerValue: unknown;
	question: QuestionDto;
}

/**
 * Stacked question/answer row for the signed-form print template.
 * @param props - Live question snapshot and raw signed answer
 * @returns Printable question block
 */
export const FormQuestion: FC<IProps> = (props: IProps) => {
	const { question, answerValue } = props;
	const labels = i18n.applicationTemplate;
	const customType = question.metadata?.customType;
	const consentText = question.metadata?.text ?? '';
	const isConsentQuestionRow = isConsentQuestion(question.questionType, customType, consentText);
	const consentBody = isConsentQuestionRow ? resolveApplicationTemplateQuestionBody(consentText) : null;
	const agreed = isConsentQuestionRow && isYesAnswer(answerValue);
	const fileRefs =
		customType === RCCustomQuestionType.FILE_UPLOAD ? collectFileRefs(answerValue) : [];

	return (
		<div css={answerCss} data-testid="application-question">
			<Typography color={ETypographyColor.primary} type={ETypography.body1}>
				{question.question}:
			</Typography>

			{consentBody?.html ? (
				<div
					className="textData"
					data-testid="application-consent-html"
					dangerouslySetInnerHTML={{
						__html: consentBody.html,
					}}
				/>
			) : null}
			{consentBody?.plainText ? (
				<div className="textData" data-testid="application-consent-plain">
					{consentBody.plainText}
				</div>
			) : null}
			<span className="answer">
				{isConsentQuestionRow ? (
					agreed ? (
						<span className="consentAgreed" data-testid="application-consent-agreed">
							<span className="checkedBox" aria-hidden>
								<BnIcon icon={Icons.check} />
							</span>
							{labels.agreed}
						</span>
					) : (
						CHARACHERS.DASH
					)
				) : (
					renderAnswer(answerValue, question, fileRefs)
				)}
			</span>
		</div>
	);
};

/**
 * Renders a non-consent answer: file upload, choice list, or formatted text.
 * @param answerValue - Raw signed answer
 * @param question - Live question snapshot (type + metadata)
 * @param fileRefs - Parsed file uploads when the question is a file upload
 * @returns Printable answer node
 */
function renderAnswer(answerValue: unknown, question: QuestionDto, fileRefs: IFileRef[]) {
	if (fileRefs.length) {
		return (
			<span className="fileAnswer" data-testid="application-file-answer">
				{fileRefs.map((fileRef, index) => {
					const showImage = isImageFile(fileRef.url) && isAllowedPrintImageUrl(fileRef.url);

					return (
						<span className="fileAnswerItem" key={`${fileRef.url}-${index}`}>
							{showImage ? <img className="fileImage" src={fileRef.url} alt={fileRef.fileName} /> : null}
							<span className="fileName">{fileRef.fileName}</span>
							{fileRef.fileName !== fileRef.url ? (
								<a className="fileUrl" href={fileRef.url} data-testid="application-file-url">
									{fileRef.url}
								</a>
							) : null}
						</span>
					);
				})}
			</span>
		);
	}

	const metadata = { customType: question.metadata?.customType };
	const value = unwrapAnswerValue(answerValue);

	if (Array.isArray(value)) {
		return (
			<span className="choiceList">
				{value.map((line, index) => (
					<span className="choiceItem" key={`${String(line)}-${index}`}>
						{getAnswerText({ answerValue: line, questionType: question.questionType }, metadata)}
					</span>
				))}
			</span>
		);
	}

	return getAnswerText({ answerValue, questionType: question.questionType }, metadata);
}
